import axios from "axios";
import { getCached, setCached, queueRequest, isOfflineMode, flushOfflineQueue, removeQueued } from "@/lib/offline";
import { installMockApi } from "@/lib/mockApi";

// Empty means same-origin, which is required by the offline EXE and self-hosted LAN mode.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;
const DEMO_FEATURE_CATALOG = [
  ["ai_chat", "المساعد الذكي", "الشات الذكي داخل التطبيق"],
  ["ai_import", "استيراد أصناف بالذكاء", "استيراد من صور وPDF بالـ AI"],
  ["ai_receipt", "قراءة إيصالات الدفع", "تحليل صورة الإيصال تلقائياً"],
  ["reports", "التقارير", "التقارير التشغيلية والمالية للشركة"],
  ["advanced_reports", "تقارير متقدمة", "أرباح · أكثر مبيعاً · تحليلات"],
  ["warehouses", "المستودعات ونقل المخزون", "إدارة المستودعات وتحويل المخزون بينها"],
  ["quick_product_add", "الإضافة السريعة للمنتجات", "إضافة صنف بالحقول الأساسية بسرعة"],
  ["bulk_add", "الإضافة الجماعية للمنتجات", "إدخال عدة أصناف دفعة واحدة"],
  ["multi_currency", "متعدد العملات", "SYP · USD · EUR · TRY + أسعار تلقائية"],
  ["bulk_import", "استيراد جماعي", "استيراد من Excel دفعة واحدة"],
  ["whatsapp_share", "مشاركة عبر واتساب", "إرسال الفواتير للعملاء"],
  ["custom_menu_items", "قائمة مخصصة", "إضافة روابط في الشريط الجانبي"],
  ["cashier_pos", "الكاشير ونقطة البيع", "واجهة الكاشير والبيع السريع"],
].map(([code, name, desc]) => ({ code, name, desc }));
const DEMO_ALL_FEATURE_CODES = DEMO_FEATURE_CATALOG.map(f => f.code);

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});
// Public customer-facing endpoints deliberately omit employee tokens and cookies.
export const publicApi = axios.create({ baseURL: API, withCredentials: false });
// Safe, browser-local demo API used only by preview builds. No real company data or server writes.
if (process.env.REACT_APP_MOCK_MODE === "true") {
  installMockApi({ api, publicApi, demoFeatureCodes: DEMO_ALL_FEATURE_CODES });
}

// attach company header from localStorage
api.interceptors.request.use((config) => {
  const cid = localStorage.getItem("company_id");
  if (cid) config.headers["X-Company-Id"] = cid;
  const token = localStorage.getItem("access_token");
  if (token) config.headers["Authorization"] = `Bearer ${token}`;
  return config;
});

api.interceptors.request.use((config) => {
  if (isOfflineMode() && !config._offlineReplay) {
    const method = (config.method || "get").toLowerCase();
    if (method !== "get" && method !== "head") {
      queueRequest(config);
      const err = new Error("تم حفظ العملية محليًا وستتم مزامنتها عند عودة الإنترنت");
      err.code = "OFFLINE_QUEUED";
      return Promise.reject(err);
    }
    const cached = getCached(config);
    if (cached) {
      config.adapter = async () => ({ data: cached.data, status: 200, statusText: "OK (offline)", headers: {}, config });
    }
  }
  return config;
});

api.interceptors.response.use((response) => {
  const method = (response.config.method || "get").toLowerCase();
  if (method === "get") setCached(response.config, response.data);
  return response;
}, async (error) => {
  // 2FA enrollment enforced by policy → send the user to set up 2FA
  if (error.response?.status === 403 && error.response?.data?.detail === "MFA_ENROLLMENT_REQUIRED") {
    const p = window.location.pathname || "";
    if (!p.startsWith("/2fa") && !p.startsWith("/login") && !p.startsWith("/auth")) {
      window.location.replace("/2fa?enroll=1");
    }
    return Promise.reject(error);
  }
  // Session expired / invalid token → NEVER mask with cache. Clear and force re-login.
  if (error.response?.status === 401) {
    try { localStorage.removeItem("access_token"); } catch (e) {}
    const p = window.location.pathname || "";
    const onAuthPage = p.startsWith("/login") || p.startsWith("/welcome") || p.startsWith("/auth");
    if (!onAuthPage) window.location.replace("/login");
    return Promise.reject(error);
  }
  if (error.config && !error.config._offlineReplay && !isOfflineMode()) {
    const method = (error.config.method || "get").toLowerCase();
    if (method !== "get" && !error.response) {
      queueRequest(error.config);
      error.code = "OFFLINE_QUEUED";
    }
    if (method === "get") {
      const cached = getCached(error.config);
      if (cached) return { data: cached.data, status: 200, statusText: "OK (cached)", headers: {}, config: error.config };
    }
  }
  return Promise.reject(error);
});

export const syncOfflineQueue = () => flushOfflineQueue((config) => api.request(config));

// Cache warming: prefetch the most-used read endpoints while online so their screens
// open offline even if the user never visited them. Fire-and-forget; each GET response
// is cached automatically by the response interceptor. Safe to call repeatedly.
const WARM_ENDPOINTS = [
  "/products", "/customers", "/suppliers", "/invoices", "/quotations",
  "/cashboxes", "/bank-accounts", "/expenses", "/warehouses", "/categories",
  "/journal/accounts", "/journal/entries", "/journal/trial-balance",
  "/currencies", "/reports/dashboard", "/dashboard/alerts", "/employees",
];
let _lastWarm = 0;
export function warmOfflineCache(force = false) {
  if (!navigator.onLine) return;
  if (!localStorage.getItem("company_id") || !localStorage.getItem("access_token")) return;
  const now = Date.now();
  if (!force && now - _lastWarm < 60000) return; // throttle to once/min
  _lastWarm = now;
  WARM_ENDPOINTS.forEach((url) => { api.get(url).catch(() => {}); });
}

// Replay a single queued operation (skips ordering); removes it on success.
export async function retryQueuedItem(item) {
  let data = item.data;
  if (typeof data === "string") { try { data = JSON.parse(data); } catch {} }
  await api.request({ method: item.method, url: item.url, data, params: item.params, _offlineReplay: true });
  removeQueued(item.id);
  window.dispatchEvent(new CustomEvent("offline-queue-changed"));
}

export function formatErr(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "خطأ غير معروف";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return String(d);
}

export function fmtMoney(n, cur = "SYP") {
  const v = Number(n || 0);
  return `${v.toLocaleString("ar-EG", { maximumFractionDigits: 2 })} ${cur}`;
}

// Invoice numbers are displayed zero-padded to 9 digits (e.g. 000000012).
export const fmtInvNum = (n) => String(n ?? 0).padStart(9, "0");

/**
 * Trigger a browser download of a UTF-8 CSV (openable in Excel/Numbers/LibreOffice).
 * The BOM (\uFEFF) ensures Arabic characters render correctly in Excel Windows.
 */
export function downloadCsv(filename, rows) {
  const escape = (v) => {
    if (v == null) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const csv = "\uFEFF" + rows.map((r) => r.map(escape).join(",")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}
